import type { ButtonHTMLAttributes, DetailedHTMLProps, MouseEvent, MouseEventHandler, PropsWithChildren} from 'react';
import { useCallback, useState } from 'react';

export interface Props extends DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
    onClick(event: MouseEvent<HTMLButtonElement>): Promise<void>;
}

export default function AsyncButton({ children, className, onClick, ...props }: PropsWithChildren<Props>) {
    const [trigger, isBusy] = useAsyncOnclick(onClick);

    return (
        <button className={className ?? 'btn-primary'} aria-disabled={isBusy} aria-busy={isBusy} onClick={trigger} {...props}>
            {children}
        </button>
    );
}

export function useAsyncOnclick<E>(onClick: (event: MouseEvent<E>) => Promise<void>) {
    const [isBusy, setIsBusy] = useState(false);
    const doClick: MouseEventHandler<E> = useCallback(
        async (e) => {
            if (isBusy) {
                e.stopPropagation();
                e.preventDefault();
            } else {
                setIsBusy(true);
                try {
                    await onClick(e);
                } finally {
                    setIsBusy(false);
                }
            }
        },
        [onClick, isBusy],
    );

    return [doClick, isBusy] as const;
}
